import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Search as SearchIcon, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ----- helpers ---------------------------------------------------------------
const numberToWords = (num) => {
  if (!num || num === 0) return 'Zero Rupees Only.';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const lt1k = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + lt1k(n % 100) : '');
  };
  const toWords = (n) => {
    if (n === 0) return '';
    const cr = Math.floor(n / 10000000);
    const lk = Math.floor((n % 10000000) / 100000);
    const th = Math.floor((n % 100000) / 1000);
    const r = n % 1000;
    let out = '';
    if (cr) out += lt1k(cr) + ' Crore ';
    if (lk) out += lt1k(lk) + ' Lakh ';
    if (th) out += lt1k(th) + ' Thousand ';
    if (r) out += lt1k(r);
    return out.trim();
  };
  const r = Math.floor(num);
  const p = Math.round((num - r) * 100);
  let out = toWords(r);
  if (out) {
    out += ' Rupees';
    if (p > 0) out += ' and ' + toWords(p) + ' Paise';
    out += ' Only.';
  }
  return out;
};

const fmt = (n) => (n == null || isNaN(n) ? '0.00' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const formatDate = (d) => {
  if (!d) return '';
  // Accept DD/MM/YYYY, YYYY-MM-DD, or ISO
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${dt.getFullYear()}`;
};

// Magazine constants -------------------------------------------------------
const MAGAZINE_INFO = {
  'Aabkari Times': {
    name: 'AABKARI TIMES',
    tagline: 'Monthly News Magazine on Excise and Alco Bev',
    address: 'Aabkari Times | 485 Mumfordganj | Prayagraj - 211002 (U.P)',
    phone: '+91 73797 00003 / +91 94537 59003',
    email: 'info@aabkaritimes.com, aabkaritimes@gmail.com',
    website: 'www.aabkaritimes.com',
    gstin: '09AIBPM4779R1ZA',
    pan: 'AIBPM4779R',
    bank: { name: 'Bank of Baroda', branch: 'Beli Road Branch, Allahabad', acName: 'Aabkari Times', acNo: '70020200000558', ifsc: 'BARB0VJBELI' },
    chequeNote: "Please note that all cheque/draft will be in favor of 'Aabkari Times' payable at Allahabad with your artwork in form of JPG/CDR via e-mail on info@aabkaritimes.com",
    refPrefix: 'AT',
    logoUrl: '/print-logos/aabkari-logo-bw.png',
    signatureUrl: '/print-logos/aabkari-sign.png',
    qrUrl: '/print-logos/aabkari-qr.png',
    qrLabel: 'Scan & Pay (Google Pay)',
  },
  'Aabkari Times ENG': {
    name: 'AABKARI TIMES ENG',
    tagline: 'Monthly News Magazine on Excise and Alco Bev (English)',
    address: 'Aabkari Times | 485 Mumfordganj | Prayagraj - 211002 (U.P)',
    phone: '+91 73797 00003 / +91 94537 59003',
    email: 'info@aabkaritimes.com, aabkaritimes@gmail.com',
    website: 'www.aabkaritimes.com',
    gstin: '09AIBPM4779R1ZA',
    pan: 'AIBPM4779R',
    bank: { name: 'Bank of Baroda', branch: 'Beli Road Branch, Allahabad', acName: 'Aabkari Times', acNo: '70020200000558', ifsc: 'BARB0VJBELI' },
    chequeNote: "Please note that all cheque/draft will be in favor of 'Aabkari Times' payable at Allahabad with your artwork in form of JPG/CDR via e-mail on info@aabkaritimes.com",
    refPrefix: 'AT',
    logoUrl: '/print-logos/aabkari-logo-bw.png',
    signatureUrl: '/print-logos/aabkari-sign.png',
    qrUrl: '/print-logos/aabkari-qr.png',
    qrLabel: 'Scan & Pay (Google Pay)',
  },
  'Sugar Times': {
    name: 'SUGAR TIMES',
    tagline: 'Magazine on Sugarcane and Ethanol Industry',
    address: 'Sugar Times | 485 Mumfordganj | Prayagraj - 211002 (U.P)',
    phone: '+91 7355453462',
    email: 'info@sugartimes.co.in, upsugartimes@gmail.com',
    website: 'sugartimes.co.in',
    gstin: '',
    pan: 'CKHPM7476B',
    bank: { name: 'UCO Bank', branch: 'Mumfordganj Branch, Allahabad', acName: 'Sugar Times', acNo: '16110210000861', ifsc: 'UCBA0001611' },
    chequeNote: "Please note that all cheque/draft will be in favor of 'Sugar Times' payable at Allahabad with your artwork in form of JPG/CDR via e-mail on info@sugartimes.co.in",
    refPrefix: 'ST',
    logoUrl: '/print-logos/sugar-logo-bw.png',
    signatureUrl: '/print-logos/sugar-sign.png',
    qrUrl: '/print-logos/sugar-qr.png',
    qrLabel: 'Scan & Pay (UPI)',
  },
};

// ----- component -------------------------------------------------------------
const PrintBillNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printType = searchParams.get('type') || 'bill';
  const isPI = printType === 'pi';

  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({ fy: '2026-27', magazine: '', bill_no: '', entity_id: '', company_name: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [copyType, setCopyType] = useState('ORIGINAL FOR RECIPIENT');
  const [withSignature, setWithSignature] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to view this page');
      navigate('/login');
    }
  }, [navigate]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...searchCriteria, document_type: isPI ? 'Proforma Invoice' : 'Bill' };
      const res = await axios.post(`${API}/bills/search`, payload);
      if (res.data.length === 0) {
        toast.info(isPI ? 'No Proforma Invoices found' : 'No bills found');
        setSearchResults([]);
      } else {
        setSearchResults(res.data);
        toast.success(`Found ${res.data.length} ${isPI ? 'PI' : 'bill'}(s)`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchCriteria({ fy: '2026-27', magazine: '', bill_no: '', entity_id: '', company_name: '' });
    setSearchResults([]);
    setSelectedBills([]);
  };

  const handlePrint = () => {
    if (selectedBills.length === 1) {
      const b = selectedBills[0];
      const prefix = (b.magazine === 'Aabkari Times' || b.magazine === 'Aabkari Times ENG') ? 'AT' : 'ST';
      document.title = isPI ? `${prefix}PI-${b.bill_no}-NEW` : `${prefix}-${b.bill_no}-NEW`;
    } else {
      document.title = isPI ? 'Proforma-Invoices-NEW' : 'Bills-NEW';
    }
    window.print();
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  // Determine GST applicability for current bill (boolean, not 0)
  const hasGST = (bill) => {
    return Boolean(
      (bill.cgst_amount && bill.cgst_amount > 0) ||
      (bill.sgst_amount && bill.sgst_amount > 0) ||
      (bill.igst_amount && bill.igst_amount > 0)
    );
  };

  const docTitle = (bill) => {
    if (isPI || bill.document_type === 'Proforma Invoice') return 'PROFORMA INVOICE';
    return hasGST(bill) ? 'TAX INVOICE' : 'INVOICE';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-100 py-8">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .bill-new, .bill-new * { visibility: visible; font-family: Arial, Helvetica, sans-serif !important; }
          .bill-new { position: absolute; left: 0; top: 0; width: 210mm !important; min-height: 297mm; padding: 12mm 12mm !important; margin: 0 !important; box-sizing: border-box; font-size: 11px !important; }
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 0; }
          .bill-new .doc-title { font-size: 22px !important; }
          .bill-new .copy-tag { font-size: 10px !important; }
          .bill-new table.invoice-tbl th { font-size: 10px !important; }
          .bill-new table.invoice-tbl td { font-size: 11px !important; }
          .bill-new .small { font-size: 10px !important; }
          .bill-new .tiny { font-size: 9px !important; }
        }
        .bill-new {
          max-width: 210mm; margin: 0 auto; background: white; padding: 24px;
          color: #111; font-family: Arial, Helvetica, sans-serif;
          font-size: 11px; line-height: 1.4;
        }
        .bill-new * { font-family: Arial, Helvetica, sans-serif; }
        .bill-new .rule { height: 1px; background: #111; }
        .bill-new .accent-rule { height: 1.5px; background: #4b5563; margin: 6px 0 10px; }
        .bill-new table.invoice-tbl { width: 100%; border-collapse: collapse; }
        .bill-new table.invoice-tbl th, .bill-new table.invoice-tbl td { border: 1px solid #555; padding: 5px 7px; vertical-align: top; font-size: 11px; }
        .bill-new table.invoice-tbl th { background: #f3f4f6; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; font-weight: 700; }
        .bill-new .label-key { font-weight: 700; color: #111; font-size: 11px; }
        .bill-new .blk { border: 1px solid #555; padding: 8px 10px; font-size: 11px; }
        .bill-new .doc-title { font-size: 24px; font-weight: 800; letter-spacing: 2px; color: #111; }
        .bill-new .copy-tag { display: inline-block; padding: 2px 6px; border: 1px solid #555; font-weight: 600; letter-spacing: 0.5px; font-size: 10px; }
        .bill-new .small { font-size: 10px; color: #1f2937; }
        .bill-new .tiny { font-size: 9px; color: #4b5563; }
        .bill-new .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .bill-new .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
        .bill-new .right { text-align: right; }
        .bill-new .center { text-align: center; }
        .bill-new .totals-row td { border: 1px solid #555; padding: 5px 8px; font-size: 11px; }
        .bill-new .grand td { background: #111 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-weight: 800; font-size: 12px; }
        .bill-new .logo-circle { width: 110px; height: 110px; border-radius: 50%; border: 1px solid #555; display: flex; align-items: center; justify-content: center; overflow: hidden; background: #fff; }
        .bill-new .logo-circle img { width: 100%; height: 100%; object-fit: contain; padding: 6px; }
        .bill-new .logo-text-mark { font-weight: 900; letter-spacing: 1px; font-size: 13px; line-height: 1.1; text-align: center; }
        .bill-new .cheque-note { font-size: 10px; font-style: italic; color: #1f2937; padding: 6px 8px; border: 1px dashed #888; margin-bottom: 8px; background: #fafafa; }
      `}</style>

      <div className="max-w-6xl mx-auto px-6">
        {/* ---- top toolbar (no print) ---- */}
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} /> Back
            </Button>
            <h1 className="text-3xl font-bold text-slate-800">
              {isPI ? 'Print Proforma Invoice (New)' : 'Print Bill (New)'}
            </h1>
          </div>
        </div>

        {/* ---- search form ---- */}
        {selectedBills.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6 no-print">
            <h2 className="text-xl font-bold mb-4 text-slate-700">Search</h2>
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Financial Year *</Label>
                  <Select value={searchCriteria.fy} onValueChange={(v) => setSearchCriteria({ ...searchCriteria, fy: v })}>
                    <SelectTrigger data-testid="fy-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2026-27">2026-27</SelectItem>
                      <SelectItem value="2025-26">2025-26</SelectItem>
                      <SelectItem value="2024-25">2024-25</SelectItem>
                      <SelectItem value="2023-24">2023-24</SelectItem>
                      <SelectItem value="2022-23">2022-23</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={searchCriteria.magazine} onValueChange={(v) => setSearchCriteria({ ...searchCriteria, magazine: v })}>
                    <SelectTrigger data-testid="magazine-select"><SelectValue placeholder="Select magazine" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{isPI ? 'PI No' : 'Bill No'}</Label>
                  <Input data-testid="bill-no-input" value={searchCriteria.bill_no} onChange={(e) => setSearchCriteria({ ...searchCriteria, bill_no: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Entity ID</Label>
                  <Input data-testid="entity-id-input" value={searchCriteria.entity_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, entity_id: e.target.value })} />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label>Company Name</Label>
                  <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} />
                </div>
              </div>
              <div className="flex space-x-3">
                <Button type="submit" disabled={loading} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="search-button">
                  <SearchIcon className="mr-2" size={18} /> {loading ? 'Searching...' : 'SEARCH'}
                </Button>
                <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">Reset</Button>
              </div>
            </form>
          </div>
        )}

        {/* ---- results list ---- */}
        {searchResults.length > 0 && selectedBills.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6 no-print">
            <h2 className="text-xl font-bold mb-4 text-slate-700">Select to Print</h2>
            <div className="space-y-2">
              {searchResults.map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 border rounded hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedBills([b])}
                  data-testid={`bill-option-${b.bill_no}`}>
                  <div>
                    <p className="font-semibold">{isPI ? 'PI' : 'Bill'} No: {String(b.bill_no).replace('.0', '')}</p>
                    <p className="text-xs text-slate-600">{b.magazine} — {b.billing_name}</p>
                  </div>
                  <span className="font-bold">₹ {fmt(b.total_amount || b.gross_amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- print toolbar + render ---- */}
        {selectedBills.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4 no-print">
              <Button onClick={handlePrint} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="print-button">
                <Printer className="mr-2" size={18} /> PRINT
              </Button>
              <Button onClick={handlePrint} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-pdf-button">
                <Download className="mr-2" size={18} /> SAVE AS PDF
              </Button>
              <Button onClick={() => setSelectedBills([])} variant="outline" data-testid="back-to-search-button">Back to Search</Button>

              <div className="flex items-center space-x-2 ml-2">
                <Label className="text-sm">Copy:</Label>
                <Select value={copyType} onValueChange={setCopyType}>
                  <SelectTrigger className="w-56" data-testid="copy-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ORIGINAL FOR RECIPIENT">ORIGINAL FOR RECIPIENT</SelectItem>
                    <SelectItem value="DUPLICATE FOR TRANSPORTER">DUPLICATE FOR TRANSPORTER</SelectItem>
                    <SelectItem value="TRIPLICATE FOR SUPPLIER">TRIPLICATE FOR SUPPLIER</SelectItem>
                    <SelectItem value="OFFICE COPY">OFFICE COPY</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <input type="checkbox" id="signature" checked={withSignature} onChange={(e) => setWithSignature(e.target.checked)} className="cursor-pointer" />
                <Label htmlFor="signature" className="cursor-pointer text-sm">With Signature line</Label>
              </div>
            </div>

            {selectedBills.map((bill, idx) => (
              <div key={idx} style={{ pageBreakAfter: idx < selectedBills.length - 1 ? 'always' : 'auto' }}>
                <BillNewTemplate bill={bill} copyType={copyType} withSignature={withSignature} isPI={isPI} docTitle={docTitle(bill)} hasGST={hasGST(bill)} />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// ----- print template --------------------------------------------------------
const BillNewTemplate = ({ bill, copyType, withSignature, isPI, docTitle, hasGST }) => {
  const mag = MAGAZINE_INFO[bill.magazine] || MAGAZINE_INFO['Aabkari Times'];
  const billNo = String(bill.bill_no || '').replace('.0', '');
  const billDate = formatDate(bill.bill_date) || formatDate(bill.created_at) || '';
  const refNo = `${mag.refPrefix}/${isPI ? 'PI' : 'INV'}/${bill.fy || ''}/${billNo}`;
  const subtotal = (bill.gross_amount || 0) - (bill.discount_amount || 0);

  // Recompute GST from RATE × NET amount (not from stored amounts which may have been
  // computed against gross by older versions). Detect the GST regime from stored values:
  //   - if any cgst/sgst stored OR billing state is UP → CGST + SGST split
  //   - else if igst stored → IGST
  //   - else no GST
  // Rate depends on product (or explicit gst_rate override stored on bill):
  //   - Event → 9% + 9% (intra) / 18% (inter), HSN 998397
  //   - Advertisement / Subscription/Advertisement → 2.5% + 2.5% (intra) / 5% (inter), HSN 998363
  //   - gst_rate override ('0' | '5' | '18') bypasses product rules
  const isEvent = String(bill.product || '') === 'Event';
  const rateOverride = String(bill.gst_rate || '').trim();
  let halfRate, fullRate, halfRateLabel, fullRateLabel, hsnSac;
  if (rateOverride === '0' || rateOverride === '5' || rateOverride === '18') {
    const pct = parseFloat(rateOverride);
    fullRate = pct / 100;
    halfRate = fullRate / 2;
    fullRateLabel = `${pct}%`;
    halfRateLabel = `${(pct / 2).toString()}%`;
    hsnSac = bill.hsn_sac || (isEvent ? '998397' : '998363');
  } else {
    halfRate = isEvent ? 0.09 : 0.025;
    fullRate = isEvent ? 0.18 : 0.05;
    halfRateLabel = isEvent ? '9%' : '2.5%';
    fullRateLabel = isEvent ? '18%' : '5%';
    hsnSac = bill.hsn_sac || (isEvent ? '998397' : '998363');
  }

  const upState = String(bill.billing_state || '').trim().toUpperCase() === 'UTTAR PRADESH' ||
                  String(bill.billing_state || '').trim().toUpperCase() === 'UP';
  const hadCGST = (bill.cgst_amount || 0) > 0 || (bill.sgst_amount || 0) > 0;
  const hadIGST = (bill.igst_amount || 0) > 0;
  const hadGST = hadCGST || hadIGST;

  let computedCGST = 0, computedSGST = 0, computedIGST = 0;
  if (hadGST) {
    if (hadCGST || (hadGST && upState)) {
      computedCGST = Math.round(subtotal * halfRate * 100) / 100;
      computedSGST = Math.round(subtotal * halfRate * 100) / 100;
    } else {
      computedIGST = Math.round(subtotal * fullRate * 100) / 100;
    }
  }
  const totalGST = computedCGST + computedSGST + computedIGST;
  const grandTotal = Math.round((subtotal + totalGST) * 100) / 100;
  const itemDesc = bill.item_description || bill.product || '—';

  const tenure = [bill.start_month, bill.start_year].filter(Boolean).join(' ') +
                 ((bill.end_month || bill.end_year) ? ' to ' + [bill.end_month, bill.end_year].filter(Boolean).join(' ') : '');

  // Detect Subscription / Renewal products to swap description + T&Cs
  // Match ONLY exact "Subscription" or "Renewal" — NOT "Subscription/Advertisement"
  const productTrim = (bill.product || '').trim().toLowerCase();
  const isRenewal = productTrim === 'renewal';
  const isPureSubscription = productTrim === 'subscription';
  const isSubscription = isRenewal || isPureSubscription;
  const productDisplay = isRenewal
    ? `Renewal of ${bill.magazine} subscription`
    : isPureSubscription
      ? `Subscription of ${bill.magazine} magazine`
      : (bill.product || itemDesc);

  // Subscription T&Cs (override stored bill.terms_conditions when product is Renewal/Subscription)
  const subscriptionTerms = [
    { title: 'Subscription Validity', body: 'The magazine subscription is valid only for the selected subscription period and is non-transferable.' },
    { title: 'No Cancellation Policy', body: 'Once the subscription is confirmed and payment is made, cancellation of the subscription is not allowed.' },
    { title: 'Non-Refundable Payment', body: 'All subscription fees paid are non-refundable, regardless of non-usage or discontinuation by the subscriber.' },
    { title: 'Right to Terminate Subscription', body: 'The publisher reserves the right to cancel or terminate the subscription without prior notice if the subscriber is found violating any terms, policies, or engaging in misuse of the magazine content or services.' },
    { title: 'Content & Usage Restrictions', body: 'All magazine content is protected under copyright laws. Unauthorized copying, sharing, reproduction, or commercial use of the content is strictly prohibited and may result in legal action and immediate cancellation of the subscription.' },
    { title: 'Change of Address', body: `Subscribers may request a change in delivery address by sending an email to ${mag.email} or contact on +91 73797 00003. Address update requests should be submitted in advance and may take time to process.` },
  ];

  return (
    <div className="bill-new" data-testid="bill-new-template">
      {/* === HEADER === */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        {/* LEFT: title + company */}
        <div style={{ flex: 1 }}>
          <div className="doc-title">{docTitle}</div>
          <div className="copy-tag" style={{ marginTop: 4 }} data-testid="copy-tag">{copyType}</div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 1 }}>{mag.name}</div>
            <div className="small" style={{ fontStyle: 'italic' }}>{mag.tagline}</div>
            <div className="small" style={{ marginTop: 2 }}>{mag.address}</div>
            <div className="small">Mob: {mag.phone} &nbsp;|&nbsp; {mag.email} &nbsp;|&nbsp; {mag.website}</div>
            {mag.gstin && <div className="small"><strong>GSTIN:</strong> {mag.gstin} &nbsp; <strong>PAN:</strong> {mag.pan}</div>}
            {!mag.gstin && <div className="small"><strong>PAN:</strong> {mag.pan}</div>}
          </div>
        </div>
        {/* RIGHT: logo + ref/date block */}
        <div style={{ width: 220, marginLeft: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div className="logo-circle">
            {mag.logoUrl ? (
              <img src={mag.logoUrl} alt={mag.name} />
            ) : (
              <div className="logo-text-mark">
                {mag.name.split(' ').map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}
          </div>
          <table className="invoice-tbl" style={{ marginTop: 8, width: '100%' }}>
            <tbody>
              <tr><td className="label-key">{isPI ? 'PI No.' : 'Invoice No.'}</td><td>{billNo}</td></tr>
              <tr><td className="label-key">Date</td><td>{billDate}</td></tr>
              <tr><td className="label-key">Reference</td><td className="small">{refNo}</td></tr>
              {bill.po_wo_no && <tr><td className="label-key">PO/WO No.</td><td className="small">{bill.po_wo_no}</td></tr>}
              {!isPI && bill.proforma_invoice_no && <tr><td className="label-key">PI Ref</td><td className="small">{bill.proforma_invoice_no}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="accent-rule" />

      {/* === BILL TO / SHIP TO === */}
      <div className="grid-2" style={{ marginBottom: 10 }}>
        <div className="blk">
          <div className="label-key" style={{ marginBottom: 4, letterSpacing: 1 }}>BILL TO</div>
          <div style={{ fontWeight: 700 }}>{bill.billing_name}</div>
          {bill.billing_address && <div className="small">{bill.billing_address}</div>}
          <div className="small">{[bill.district, bill.billing_state, bill.pincode].filter(Boolean).join(', ')}</div>
          {bill.gst_no && <div className="small"><strong>GSTIN:</strong> {bill.gst_no}</div>}
        </div>
        <div className="blk">
          <div className="label-key" style={{ marginBottom: 4, letterSpacing: 1 }}>SHIP TO</div>
          <div style={{ fontWeight: 700 }}>{bill.bill_receipt_name || bill.billing_name}</div>
          {bill.billing_address && <div className="small">{bill.billing_address}</div>}
          <div className="small">{[bill.district, bill.billing_state, bill.pincode].filter(Boolean).join(', ')}</div>
          <div className="small"><strong>State:</strong> {bill.billing_state}</div>
        </div>
      </div>

      {/* === ITEMS TABLE === */}
      <table className="invoice-tbl" style={{ marginBottom: 10 }}>
        <thead>
          <tr>
            <th style={{ width: '6%' }} className="center">#</th>
            <th style={{ width: '50%' }}>Description of Goods / Services</th>
            <th style={{ width: '12%' }} className="center">HSN/SAC</th>
            <th style={{ width: '10%' }} className="center">Duration</th>
            <th style={{ width: '10%' }} className="right">Rate (₹)</th>
            <th style={{ width: '12%' }} className="right">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="center">1</td>
            <td>
              <div style={{ fontWeight: 700 }}>{productDisplay}</div>
              {bill.advertisement_position && <div className="small">Position: {bill.advertisement_position}</div>}
              {bill.advertisement_tenure && <div className="small">Tenure: {bill.advertisement_tenure}</div>}
              {tenure && <div className="small">Period: {tenure}</div>}
              {bill.occasion && bill.occasion !== 'Regular Issue' && <div className="small">Occasion: {bill.occasion}</div>}
              {bill.item_description && bill.item_description !== bill.product && <div className="small">{bill.item_description}</div>}
              {bill.include_in_aabkari && bill.magazine === 'Sugar Times' && <div className="small" style={{ fontStyle: 'italic' }}>+ Aabkari Times (Complimentary)</div>}
            </td>
            <td className="center">{hsnSac}</td>
            <td className="center">{bill.advertisement_tenure || '1 Month'}</td>
            <td className="right">{fmt(bill.gross_amount)}/-</td>
            <td className="right">{fmt(bill.gross_amount)}/-</td>
          </tr>
          {/* spacer rows for visual fill */}
          {[...Array(2)].map((_, i) => (
            <tr key={`s${i}`} style={{ height: 18 }}>
              <td className="center" style={{ color: '#bbb' }}>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="totals-row">
            <td colSpan="5" className="right label-key">Gross Amount</td>
            <td className="right">{fmt(bill.gross_amount)}/-</td>
          </tr>
          {(bill.discount_amount || bill.discount_percentage) ? (
            <tr className="totals-row">
              <td colSpan="5" className="right label-key">
                Discount{bill.discount_percentage ? ` (${bill.discount_percentage}%)` : ''}
              </td>
              <td className="right">- {fmt(bill.discount_amount)}/-</td>
            </tr>
          ) : null}
          {(bill.discount_amount || bill.discount_percentage) ? (
            <tr className="totals-row">
              <td colSpan="5" className="right label-key">Sub-Total (Taxable Value)</td>
              <td className="right">{fmt(subtotal)}/-</td>
            </tr>
          ) : null}
          {hadGST && computedSGST > 0 && (
            <tr className="totals-row">
              <td colSpan="5" className="right label-key">SGST @ {halfRateLabel}</td>
              <td className="right">{fmt(computedSGST)}/-</td>
            </tr>
          )}
          {hadGST && computedCGST > 0 && (
            <tr className="totals-row">
              <td colSpan="5" className="right label-key">CGST @ {halfRateLabel}</td>
              <td className="right">{fmt(computedCGST)}/-</td>
            </tr>
          )}
          {hadGST && computedIGST > 0 && (
            <tr className="totals-row">
              <td colSpan="5" className="right label-key">IGST @ {fullRateLabel}</td>
              <td className="right">{fmt(computedIGST)}/-</td>
            </tr>
          )}
          <tr className="totals-row grand">
            <td colSpan="5" className="right">GRAND TOTAL (₹)</td>
            <td className="right">{fmt(grandTotal)}/-</td>
          </tr>
        </tfoot>
      </table>

      {/* === Amount in words === */}
      <div className="blk" style={{ marginBottom: 10 }}>
        <span className="label-key">Amount in Words: </span>
        <span style={{ fontStyle: 'italic' }}>{numberToWords(grandTotal)}</span>
      </div>

      {/* === Tax breakup (only for GST bills) === */}
      {hadGST && (
        <table className="invoice-tbl" style={{ marginBottom: 10 }}>
          <thead>
            <tr>
              <th>HSN/SAC</th>
              <th className="right">Taxable Value</th>
              <th className="center">CGST %</th>
              <th className="right">CGST Amt</th>
              <th className="center">SGST %</th>
              <th className="right">SGST Amt</th>
              <th className="center">IGST %</th>
              <th className="right">IGST Amt</th>
              <th className="right">Total Tax</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="center">{hsnSac}</td>
              <td className="right">{fmt(subtotal)}</td>
              <td className="center">{computedCGST > 0 ? halfRateLabel : '-'}</td>
              <td className="right">{fmt(computedCGST)}</td>
              <td className="center">{computedSGST > 0 ? halfRateLabel : '-'}</td>
              <td className="right">{fmt(computedSGST)}</td>
              <td className="center">{computedIGST > 0 ? fullRateLabel : '-'}</td>
              <td className="right">{fmt(computedIGST)}</td>
              <td className="right">{fmt(totalGST)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* === Cheque/Draft note === */}
      {mag.chequeNote && (
        <div className="cheque-note">{mag.chequeNote}</div>
      )}

      {/* === FOOTER: Bank + Terms + Signature === */}
      <div className="grid-2" style={{ marginBottom: 8 }}>
        <div className="blk" style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="label-key" style={{ marginBottom: 3, letterSpacing: 1 }}>BANK DETAILS</div>
            <div><strong>Account Name:</strong> {mag.bank.acName}</div>
            <div><strong>Bank:</strong> {mag.bank.name}, {mag.bank.branch}</div>
            <div><strong>A/c No:</strong> {mag.bank.acNo}</div>
            <div><strong>IFSC:</strong> {mag.bank.ifsc}</div>
            {mag.gstin && <div style={{ marginTop: 4 }}><strong>GSTIN:</strong> {mag.gstin}</div>}
            <div><strong>PAN:</strong> {mag.pan}</div>
          </div>
          {mag.qrUrl && (
            <div style={{ width: 120, textAlign: 'center', flexShrink: 0, borderLeft: '1px dashed #888', paddingLeft: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 10 }}>{mag.qrLabel}</div>
              <img src={mag.qrUrl} alt="Pay QR" style={{ width: 100, height: 100, objectFit: 'contain', display: 'block', margin: '2px auto' }} />
            </div>
          )}
        </div>
        <div className="blk" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div className="label-key" style={{ marginBottom: 3, letterSpacing: 1 }}>DECLARATION</div>
            <div>
              We declare that this {docTitle.toLowerCase()} shows the actual price of the {isPI ? 'proposed' : 'goods/services'} described and that all particulars are true and correct.
              {isPI ? ' This is a Proforma Invoice and is not a tax invoice.' : ''}
            </div>
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <div>For <strong>{mag.name}</strong></div>
            {withSignature && mag.signatureUrl ? (
              <img
                src={mag.signatureUrl}
                alt="Signature"
                style={{ height: 70, marginTop: 4, marginLeft: 'auto', display: 'block', objectFit: 'contain', maxWidth: 200 }}
              />
            ) : (
              <div style={{ height: 38 }} />
            )}
            <div className="rule" style={{ width: 160, marginLeft: 'auto', marginTop: 2 }} />
            <div style={{ marginTop: 2 }}>Authorised Signatory</div>
          </div>
        </div>
      </div>

      {/* === Terms === */}
      {isSubscription ? (
        <div className="blk" style={{ marginBottom: 8 }}>
          <div className="label-key" style={{ marginBottom: 4, letterSpacing: 1 }}>TERMS & CONDITIONS</div>
          <ol className="small" style={{ paddingLeft: 16, margin: 0 }}>
            {subscriptionTerms.map((t, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <strong>{t.title}:</strong> {t.body}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        bill.terms_conditions && (
          <div className="blk" style={{ marginBottom: 8 }}>
            <div className="label-key" style={{ marginBottom: 3, letterSpacing: 1 }}>TERMS & CONDITIONS</div>
            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{bill.terms_conditions}</div>
          </div>
        )
      )}

      <div className="accent-rule" />

      {/* === Footer line === */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="small">
        <span>Subject to High Court of Allahabad Jurisdiction</span>
        <span style={{ fontStyle: 'italic' }}>This is a Computer Generated {isPI ? 'Proforma Invoice' : 'Invoice'}</span>
      </div>
    </div>
  );
};

export default PrintBillNew;
